import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { URL_EMAILS } from '../../constants/urls';
import style from '../../style/components/profile/EmailVerificationModal.module.scss';
import StyledModal from '../StyledModal';

const OTP_LENGTH = 6;
const emptyCode = () => Array(OTP_LENGTH).fill('');

const EmailVerificationModal = ({
  isOpen,
  closeModal,
  inspectionId,
  emailAddress,
  onVerifySuccess,
  onLockoutClose,
}) => {
  const [code, setCode] = useState(emptyCode());
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [showSuccess, setShowSuccess] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const inputRefs = useRef([]);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pollingRef = useRef(null);
  const pollingAbortRef = useRef(null);
  const isMountedRef = useRef(true);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    setCountdown(60); // Reset on open

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, resetTrigger]);

  // Reset form state when modal opens or inspectionId changes
  useEffect(() => {
    if (isOpen) {
      setCode(emptyCode());
      setError('');
      setShowSuccess(false);
      setIsVerifying(false);
      setIsResending(false);
      setIsLockedOut(false);
      setRemainingAttempts(null);
      inputRefs.current[0]?.focus();
    }
  }, [isOpen, inspectionId]);

  // Master cleanup effect — runs on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (pollingAbortRef.current) {
        pollingAbortRef.current.abort();
        pollingAbortRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (pollingAbortRef.current) {
      pollingAbortRef.current.abort();
      pollingAbortRef.current = null;
    }
  }, []);

  // Poll for cross-device verification status
  useEffect(() => {
    if (!isOpen || !inspectionId) {
      stopPolling();
      return;
    }

    const poll = async () => {
      if (!isMountedRef.current) return;

      // Abort any in-flight polling request before starting a new one
      if (pollingAbortRef.current) {
        pollingAbortRef.current.abort();
      }
      pollingAbortRef.current = new AbortController();
      try {
        const response = await axios.get(
          `${URL_EMAILS}/${inspectionId}/verification_status`,
          {
            signal: pollingAbortRef.current.signal,
          },
        );

        if (!isMountedRef.current) return;

        if (
          response.data.status === 'completed' &&
          response.data.outcome === 'passed'
        ) {
          stopPolling();
          setShowSuccess(true);
          timeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setShowSuccess(false);
            onVerifySuccess();
          }, 2000);
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (!isMountedRef.current) return;
        const status = err.response?.status;
        if (status === 401 || status === 404 || status === 410) {
          stopPolling();
          setError(
            'This verification session has expired. Please close and try again.',
          );
        }
        // Transient failures (network, 5xx) — continue polling silently
      }
    };

    pollingRef.current = setInterval(poll, 3000);

    return () => stopPolling();
  }, [isOpen, inspectionId, onVerifySuccess, stopPolling]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take last character
    setCode(newCode);
    setError(''); // Clear error on input

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (index === OTP_LENGTH - 1 && value) {
      const fullCode = newCode.join('');
      handleVerify(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace to previous field
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Arrow key navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');

    if (digits.length === 0) return;

    const newCode = emptyCode();
    digits.forEach((digit, i) => {
      newCode[i] = digit;
    });
    setCode(newCode);
    setError('');

    // Focus the input after the last pasted digit, or the last input
    const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if all digits are filled
    if (digits.length === OTP_LENGTH) {
      handleVerify(newCode.join(''));
    }
  };

  const handleVerify = useCallback(
    async (fullCode) => {
      setIsVerifying(true);
      setError('');

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      try {
        const response = await axios.post(
          `${URL_EMAILS}/${inspectionId}/verify_code`,
          { code: fullCode },
          { signal: abortControllerRef.current.signal },
        );

        if (!isMountedRef.current) return;

        if (response.data.success) {
          // Stop polling — manual OTP succeeded
          stopPolling();
          // Show success message
          setShowSuccess(true);

          // Wait 2s then notify parent (parent handles closing the modal)
          timeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setShowSuccess(false);
            onVerifySuccess();
          }, 2000);
        } else {
          const { error_code, remaining_attempts: attemptsLeft } =
            response.data;
          if (error_code === 'RATE_LIMIT_EXCEEDED' || attemptsLeft === 0) {
            setIsLockedOut(true);
            setRemainingAttempts(0);
            setError('');
          } else {
            if (attemptsLeft != null) {
              setRemainingAttempts(attemptsLeft);
            }
            setError(response.data.error || 'Invalid code. Please try again.');
          }
          setCode(emptyCode());
          inputRefs.current[0]?.focus();
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (!isMountedRef.current) return;
        const data = err.response?.data;
        if (data) {
          if (
            data.error_code === 'RATE_LIMIT_EXCEEDED' ||
            data.remaining_attempts === 0
          ) {
            setIsLockedOut(true);
            setRemainingAttempts(0);
            setError('');
          } else {
            if (data.remaining_attempts != null) {
              setRemainingAttempts(data.remaining_attempts);
            }
            setError(data.error || 'Invalid code. Please try again.');
          }
        } else {
          setError('Network error. Please try again.');
        }
        setCode(emptyCode());
        inputRefs.current[0]?.focus();
      } finally {
        if (isMountedRef.current) {
          setIsVerifying(false);
        }
      }
    },
    [inspectionId, onVerifySuccess, stopPolling],
  );

  const handleResend = useCallback(async () => {
    if (isResending) return;

    setIsResending(true);
    setError('');
    setCode(emptyCode());
    inputRefs.current[0]?.focus();

    try {
      const response = await axios.post(`${URL_EMAILS}/${inspectionId}/resend`);

      if (!isMountedRef.current) return;

      if (response.data.success) {
        setResetTrigger((prev) => prev + 1);
      } else {
        if (response.data.error_code === 'RATE_LIMIT_EXCEEDED') {
          setIsLockedOut(true);
        }
        setError(response.data.error || 'Failed to resend code.');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const data = err.response?.data;
      if (data) {
        if (data.error_code === 'RATE_LIMIT_EXCEEDED') {
          setIsLockedOut(true);
        }
        setError(data.error || 'Failed to resend code.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsResending(false);
      }
    }
  }, [inspectionId, isResending]);

  const handleClose = () => {
    const wasLockedOut = isLockedOut;
    abortControllerRef.current?.abort();
    setCode(emptyCode());
    setError('');
    setCountdown(60);
    setShowSuccess(false);
    setIsVerifying(false);
    setIsResending(false);
    setIsLockedOut(false);
    setRemainingAttempts(null);
    if (wasLockedOut && onLockoutClose) {
      onLockoutClose();
    } else {
      closeModal();
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeModal={handleClose}
      title={showSuccess ? 'Success!' : 'Confirm your email'}
      cancelButton={{ hidden: true }}
    >
      {showSuccess ? (
        <div className={style.successMessage}>
          <idme-icon name="check-circle" size="large" />
          <p>Email confirmed!</p>
        </div>
      ) : (
        <>
          <p className={style.instructions}>
            Select the link in the email we sent to{' '}
            <strong>{emailAddress}</strong>, or enter the code.
          </p>

          <div
            className={style.codeInputContainer}
            role="group"
            aria-label="Verification code"
          >
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                className={style.codeInput}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isVerifying || isLockedOut}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {error && (
            <div className={style.error} role="alert">
              {error}
            </div>
          )}

          {remainingAttempts != null && remainingAttempts > 0 && (
            <div className={style.attemptsWarning} role="status">
              {remainingAttempts}{' '}
              {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining
            </div>
          )}

          {isLockedOut ? (
            <div className={style.resendContainer}>
              <p className={style.lockoutMessage}>
                You've used all verification attempts. Please close this dialog
                and click "Confirm email" to try again.
              </p>
            </div>
          ) : (
            <div className={style.resendContainer}>
              {countdown > 0 ? (
                <span className={style.countdown} aria-live="polite">
                  Didn't get the email? Please wait {countdown}s
                </span>
              ) : (
                <button
                  type="button"
                  className={style.resendLink}
                  onClick={handleResend}
                  disabled={isResending}
                >
                  {isResending ? 'Sending...' : 'Resend'}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </StyledModal>
  );
};

EmailVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  inspectionId: PropTypes.string.isRequired,
  emailAddress: PropTypes.string.isRequired,
  onVerifySuccess: PropTypes.func.isRequired,
  onLockoutClose: PropTypes.func,
};

export default EmailVerificationModal;
